/**
 * useVfsUpload
 *
 * Three-step presigned upload (init → PUT to S3 → complete) for one VFS
 * owner, with per-file progress and AbortController support. Mirrors
 * `useUploadSpaceDocument` (src/hooks/useDocuments.ts) but targets the
 * unified `/api/v1/vfs/{ownerId}/...` routes.
 *
 * The completed file does not need to be added to local state — the
 * backend emits `vfs.file.created`, which `useVfsFiles` picks up.
 */

import { useCallback, useRef, useState } from 'react';
import { vfsApi } from '../services/api';
import type { VfsEntry } from '../types/vfs';

export interface VfsUploadJob {
  file: File;
  /** Final path inside the VFS (incl. any folder prefix). */
  path: string;
  progress: number; // 0–1
  status: 'pending' | 'uploading' | 'completing' | 'done' | 'error';
  error?: string;
  result?: VfsEntry;
}

export interface UseVfsUploadResult {
  jobs: VfsUploadJob[];
  /**
   * Upload a file into the VFS.
   * - `prefix`: optional folder ("uploads", "reports/q1"), no leading/trailing slash needed.
   * - `name`: override the basename (use for collision-resolved names like
   *   `data-2.csv`). Defaults to `file.name`.
   */
  uploadFile: (file: File, opts?: { prefix?: string; name?: string }) => Promise<VfsEntry | null>;
  cancel: (path: string) => void;
  clearDone: () => void;
}

/**
 * Sanitize a filename to be path-safe. Drops control chars + collapses
 * separators. The backend will reject anything truly invalid.
 */
export function safePathSegment(name: string): string {
  return name
    // eslint-disable-next-line no-control-regex
    .replace(/[ -]/g, '')
    .replace(/[\\/]+/g, '_')
    .replace(/^\.+/, '')
    .trim();
}

export function joinVfsPath(prefix: string | undefined, name: string): string {
  const segment = safePathSegment(name) || 'untitled';
  if (!prefix) return segment;
  const trimmed = prefix.replace(/^\/+|\/+$/g, '');
  return trimmed ? `${trimmed}/${segment}` : segment;
}

export function useVfsUpload(ownerId: string | undefined): UseVfsUploadResult {
  const [jobs, setJobs] = useState<VfsUploadJob[]>([]);
  const aborts = useRef<Map<string, XMLHttpRequest>>(new Map());

  const updateJob = useCallback((path: string, patch: Partial<VfsUploadJob>) => {
    setJobs((prev) => prev.map((j) => (j.path === path ? { ...j, ...patch } : j)));
  }, []);

  const uploadFile = useCallback(
    async (file: File, opts?: { prefix?: string; name?: string }): Promise<VfsEntry | null> => {
      if (!ownerId) return null;
      const path = joinVfsPath(opts?.prefix, opts?.name ?? file.name);

      setJobs((prev) => {
        const without = prev.filter((j) => j.path !== path);
        return [...without, { file, path, progress: 0, status: 'pending' }];
      });

      try {
        updateJob(path, { status: 'uploading' });

        const init = await vfsApi.initUpload(ownerId, {
          path,
          contentType: file.type || 'application/octet-stream',
        });

        // Use XHR for upload progress; fetch() doesn't expose it.
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          aborts.current.set(path, xhr);
          xhr.open('PUT', init.url, true);
          // Echo init-upload's headers verbatim — the presigned URL signature
          // is bound to them, so don't add or override anything (including
          // Content-Type) on top.
          for (const [k, v] of Object.entries(init.headers ?? {})) {
            xhr.setRequestHeader(k, v);
          }
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              updateJob(path, { progress: e.loaded / e.total });
            }
          };
          xhr.onload = () => {
            aborts.current.delete(path);
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`Upload failed (${xhr.status})`));
          };
          xhr.onerror = () => {
            aborts.current.delete(path);
            reject(new Error('Upload network error'));
          };
          xhr.onabort = () => {
            aborts.current.delete(path);
            reject(new Error('Upload cancelled'));
          };
          xhr.send(file);
        });

        updateJob(path, { status: 'completing', progress: 1 });
        const entry = await vfsApi.completeUpload(ownerId, {
          path,
          fileId: init.fileId,
          contentType: file.type || undefined,
        });
        updateJob(path, { status: 'done', result: entry });
        return entry;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        updateJob(path, { status: 'error', error: message });
        return null;
      }
    },
    [ownerId, updateJob],
  );

  const cancel = useCallback((path: string) => {
    const xhr = aborts.current.get(path);
    if (xhr) xhr.abort();
  }, []);

  const clearDone = useCallback(() => {
    setJobs((prev) => prev.filter((j) => j.status !== 'done'));
  }, []);

  return { jobs, uploadFile, cancel, clearDone };
}
